import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router";
import { fetchPatientList } from "../../../Redux/PatientSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  addPrescription,
  createMedicationName,
  fetchMedicationNames,
} from "../../../Redux/PrescriptionSlice";
import { toast } from "react-toastify";
import { ColorRing } from "react-loader-spinner";
import Breadcrumb from "../../dataUi/Breadcrumb";

const AddPrescription = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  // Form state
  const [orderSource, setOrderSource] = useState("provider"); // Default to 'provider'
  const [prescriptionType, setPrescriptionType] = useState("");
  const [selectedPatientId, setSelectedPatientId] = useState("");
  const [selectedPatientDetails, setSelectedPatientDetails] = useState(null);
  const [comment, setComment] = useState("");

  // Form progress tracking - only two steps now
  const [step1Complete, setStep1Complete] = useState(false); // Order source, provider info and patient info
  const [step2Complete, setStep2Complete] = useState(false); // Medications

  // Redux state
  const { loading } = useSelector((state) => state.prescriptions);
  const medicationNames = useSelector((state) => state.prescriptions.medicationNames);
  // Provider info now maps to prescription_provider fields
  const [providerInfo, setProviderInfo] = useState({
    first_name: "",
    last_name: "",
    title: "",
    provider_id: "",
    phone_number: "",
    facility: "",
  });
  const [medications, setMedications] = useState([
    {
      medication_name_id: "",
      custom_medication_name: "",
      showMedicationDropdown: false,
      dosage_form: "",
      strength: "",
      route: "",
      frequency: "",
      quantity: 0,
      unit: "",
      refills: 0,
      instructions: "",
    },
  ]);
  // Check form completion status for progress bars
  useEffect(() => {
    // Step 1 is complete when orderSource is selected,
    // patient is selected, and provider info is valid if needed
    setStep1Complete(
      orderSource !== "" &&
      selectedPatientId !== "" &&
      (orderSource === "patient" ||
        (prescriptionType !== "" && isProviderInfoValid()))
    );
    // Step 2 is complete when all medications have valid data
    const allMedicationsValid = !medications.some(
      (med) => {
        // Either medication_name_id must be valid or custom_medication_name must be filled
        const hasMedicationName = med.medication_name_id ||
          (med.custom_medication_name && med.custom_medication_name.trim() !== "");

        return !hasMedicationName ||
          !med.dosage_form ||
          !med.strength ||
          !med.route ||
          !med.frequency ||
          !med.quantity ||
          med.quantity <= 0 ||
          !med.unit;
      }
    );
    setStep2Complete(allMedicationsValid && medications.length > 0);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    orderSource,
    prescriptionType,
    selectedPatientId,
    providerInfo,
    medications,
  ]);

  // Get patient info from location state if available
  useEffect(() => {
    if (location.state?.patientInfo) {
      const { id, dob, gender, phone } = location.state.patientInfo;
      setSelectedPatientId(id);
      setSelectedPatientDetails({
        dob,
        gender,
        phone
      });
    }
  }, [location.state]);

  const handleProviderInfoChange = (field, value) => {
    setProviderInfo({ ...providerInfo, [field]: value });
  };
  const handleMedicationChange = (index, field, value) => {
    const updatedMedications = [...medications];

    if (field === "showRefills") {
      updatedMedications[index][field] = value;
    } else if (field === "quantity" || field === "refills") {
      // Convert to number for numeric fields
      updatedMedications[index][field] = value === "" ? 0 : Number(value);
    } else if (field === "strength") {
      // Keep strength as a string, don't convert to number
      updatedMedications[index][field] = value.toString();
    } else {
      updatedMedications[index][field] = value;
    }

    setMedications(updatedMedications);
  };
  const addMedication = () => {
    setMedications([
      ...medications,
      {
        medication_name_id: "",
        custom_medication_name: "",
        showMedicationDropdown: false,
        dosage_form: "",
        strength: "",
        route: "",
        frequency: "",
        quantity: 0,
        unit: "",
        refills: 0,
        instructions: "",
      },
    ]);
  };
  useEffect(() => {
    dispatch(fetchPatientList())
      .unwrap()
      .then()
      .catch((error) => console.error("Error fetching patients:", error));

    // Fetch medication names with proper handling of the response format
    console.log("Fetching medication names...");
    dispatch(fetchMedicationNames())
      .unwrap()
      .then((data) => {
        console.log("Medication names data received:", data);
        // Check what format the data is in
        if (Array.isArray(data)) {
          // Direct array format
          console.log("Number of medication names:", data.length);
          console.log("First few medication names:", data.slice(0, 5).map(med => med.name));
        } else if (data?.data && Array.isArray(data.data)) {
          // Wrapped in data property
          console.log("Number of medication names:", data.data.length);
          console.log("First few medication names:", data.data.slice(0, 5).map(med => med.name));
        } else {
          console.error("Unexpected medication data format:", data);
        }
      })
      .catch((error) => {
        // Fix: Don't log the error object directly, stringify it for debugging
        console.error("Error fetching medication names:", error.message || "Unknown error");
      });
  }, [dispatch]);

  // Check if required provider fields are filled based on order_source
  const isProviderInfoValid = () => {
    if (orderSource === "patient") {
      // Provider is optional when order_source is patient
      return true;
    }    // When order_source is provider, we need at least first_name, last_name, and provider_id
    return (
      providerInfo.first_name !== "" &&
      providerInfo.last_name !== "" &&
      providerInfo.provider_id !== ""
    );
  };
  const handleSubmit = async () => {
    try {
      // Basic validation
      if (!selectedPatientId) {
        toast.warn("Please select a patient");
        return;
      }

      // Validate medications
      if (!medications || medications.length === 0) {
        toast.warn("Please add at least one medication");
        return;
      }

      // Validate each medication
      for (let i = 0; i < medications.length; i++) {
        const med = medications[i];
        if (!med.medication_name_id && !med.custom_medication_name) {
          toast.warn(`Please select or enter a medication name for medication #${i + 1}`);
          return;
        }
        if (!med.dosage_form) {
          toast.warn(`Please select a dosage form for medication #${i + 1}`);
          return;
        }
        if (!med.strength) {
          toast.warn(`Please enter a strength for medication #${i + 1}`);
          return;
        }
        if (!med.quantity || med.quantity <= 0) {
          toast.warn(`Please enter a valid quantity for medication #${i + 1}`);
          return;
        }
        if (!med.unit) {
          toast.warn(`Please select a unit for medication #${i + 1}`);
          return;
        }
        if (!med.route) {
          toast.warn(`Please select a route for medication #${i + 1}`);
          return;
        }
        if (!med.frequency) {
          toast.warn(`Please select a frequency for medication #${i + 1}`);
          return;
        }
      }

      // Validate provider info if order source is provider
      if (orderSource === "provider") {
        if (!prescriptionType) {
          toast.warn("Please select a prescription type");
          return;
        }
        if (!providerInfo.first_name || !providerInfo.last_name || !providerInfo.provider_id) {
          toast.warn("Please fill in all required provider information");
          return;
        }
      }

      // Format medications data
      const formattedMedications = await Promise.all(medications.map(async med => {
        let medication_name_id = med.medication_name_id;

        // If no medication_name_id but has custom name, create new medication
        if (!medication_name_id && med.custom_medication_name) {
          try {
            const result = await dispatch(createMedicationName({
              name: med.custom_medication_name.trim()
            })).unwrap();

            medication_name_id = result.id || (result.data && result.data.id);

            if (!medication_name_id) {
              throw new Error("Failed to create medication name");
            }
          } catch (error) {
            console.error("Error creating medication name:", error);
            throw new Error(`Failed to create medication: ${error.message}`);
          }
        }

        // Ensure medication_name_id is a number
        if (medication_name_id) {
          medication_name_id = Number(medication_name_id);
        }

        return {
          medication_name_id,
          strength: String(med.strength),
          quantity: Number(med.quantity),
          refills: Number(med.refills || 0),
          instructions: med.instructions || "Take as prescribed",
          unit: med.unit,
          dosage_form: med.dosage_form,
          route: med.route,
          frequency: med.frequency
        };
      }));

      // Build the prescription data object
      const prescriptionData = {
        order_source: orderSource,
        type: orderSource === "patient" ? null : prescriptionType.toUpperCase(),
        patient_id: Number(selectedPatientId),
        date_issued: new Date().toISOString().split('T')[0],
        status: 'pending', // Set initial status to pending
        medications: formattedMedications,
        comment: comment || "",
        provider: orderSource === "provider" ? {
          first_name: providerInfo.first_name || "",
          last_name: providerInfo.last_name || "",
          title: providerInfo.title || "",
          provider_id: providerInfo.provider_id || "",
          phone_number: providerInfo.phone_number || "",
          facility: providerInfo.facility || ""
        } : null
      };

      // Log the full request payload for debugging
      console.log("Final prescription data:", JSON.stringify(prescriptionData, null, 2));

      const resultAction = await dispatch(addPrescription(prescriptionData));

      if (addPrescription.fulfilled.match(resultAction)) {
        toast.success("Prescription added successfully");

        // Reset form
        setPrescriptionType("");
        setOrderSource("provider");
        setSelectedPatientId("");
        setComment("");
        setProviderInfo({
          first_name: "",
          last_name: "",
          title: "",
          provider_id: "",
          phone_number: "",
          facility: ""
        });
        setMedications([{
          medication_name_id: "",
          dosage_form: "",
          strength: "",
          route: "",
          frequency: "",
          quantity: 0,
          unit: "",
          refills: 0,
          instructions: "",
        }]);

        // Navigate back with required roles
        setTimeout(() => {
          navigate(-1, {
            state: { requiredRoles: ['prescriptionViewer', 'prescriptionWriter', 'prescriptionAdmin'] }
          });
        }, 100);
      } else {
        const errorMessage = typeof resultAction.payload === 'object' && resultAction.payload !== null
          ? (resultAction.payload.message || JSON.stringify(resultAction.payload))
          : (resultAction.error?.message || 'Unknown error');

        toast.error(`Failed to add prescription: ${errorMessage}`);
      }
    } catch (error) {
      console.error("Failed to add prescription:", error);
      const errorMessage = error && typeof error === 'object'
        ? (error.message || JSON.stringify(error))
        : 'Unknown error occurred';
      toast.error(`Error: ${errorMessage}`);
    }
  };

  const breadcrumbItems = [
    {
      label: "Prescriptions",
      onClick: () => navigate("/retail/prescriptions"),
    },
    {
      label: "Patient info",
      onClick: () => navigate(-1),
    },
    {
      label: "Add prescription",
    },
  ];

  const removeMedication = (index) => {
    if (medications.length > 1) {
      const updatedMedications = [...medications];
      updatedMedications.splice(index, 1);
      setMedications(updatedMedications);
    }
  };

  return (
    <div className="w-full px-5 mb-5 font-lufga">
      <div className="mb-2 space-y-2">
        <p className="text-2xl font-semibold text-slate-600">
          Add Prescription
        </p>
        <Breadcrumb
          items={breadcrumbItems}
          className="flex items-center text-sm text-green-500 transition-all duration-300 hover:text-green-600"
        />
      </div>
      {/* Progress bars section */}
      <div className="mt-4 mb-6">
        {" "}
        <div className="flex items-end justify-end mb-1 text-sm">
          <div className="text-gray-600">
            {Math.round(
              (((step1Complete ? 1 : 0) + (step2Complete ? 1 : 0)) / 2) * 100
            )}
            %
          </div>
        </div>{" "}
        <div className="flex w-full gap-4">
          {/* Step 1: Patient & Provider Info */}
          <div className="flex-1">
            <div className="flex justify-between mb-1 text-xs">
              <span>Step 1: Patient & Provider</span>
              <span>{step1Complete ? "✓" : ""}</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full">
              <div
                className={`h-2 rounded-full ${step1Complete ? "bg-green-500" : "bg-gray-400"
                  }`}
                style={{ width: step1Complete ? "100%" : "0%" }}
              ></div>
            </div>
          </div>

          {/* Step 2: Medications */}
          <div className="flex-1">
            <div className="flex justify-between mb-1 text-xs">
              <span>Step 2: Medications</span>
              <span>{step2Complete ? "✓" : ""}</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full">
              <div
                className={`h-2 rounded-full ${step2Complete ? "bg-green-500" : "bg-gray-400"
                  }`}
                style={{ width: step2Complete ? "100%" : "0%" }}
              ></div>
            </div>
          </div>
        </div>
      </div>
      <div>
        <form className="w-full mx-auto">
          {/* Patient Info Section */}
          <div className="w-full p-3 border-b rounded-md shadow bg-gradient-to-r from-green-50 to-green-100">
            <h2 className="mb-3 text-lg font-semibold text-green-800">Patient Information</h2>
            <div className="relative">
              <div className="w-full px-4 py-3 bg-white border border-green-200 rounded-lg shadow-sm">
                {selectedPatientDetails ? (
                  <div className="space-y-2 text-gray-700">
                    <div className="flex flex-wrap gap-x-4 gap-y-2">
                      <div className="flex-1 min-w-[200px]">
                        <span className="text-sm font-medium text-green-600">Patient Name</span>
                        <p className="text-base">{location.state?.patientInfo?.name || 'N/A'}</p>
                      </div>
                      <div className="flex-1 min-w-[200px]">
                        <span className="text-sm font-medium text-green-600">Date of Birth</span>
                        <p className="text-base">{new Date(selectedPatientDetails.dob).toLocaleDateString() || 'N/A'}</p>
                      </div>
                      <div className="flex-1 min-w-[200px]">
                        <span className="text-sm font-medium text-green-600">Gender</span>
                        <p className="text-base">{selectedPatientDetails.gender || 'N/A'}</p>
                      </div>
                      <div className="flex-1 min-w-[200px]">
                        <span className="text-sm font-medium text-green-600">Contact</span>
                        <p className="text-base">{selectedPatientDetails.phone || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="italic text-gray-500">No patient selected</div>
                )}
              </div>
            </div>
          </div>
          {/* Order Source - First and standalone */}
          <div className="flex flex-col items-start gap-4 mt-4 mb-4 md:flex-row">
            <div className="w-full">
              <label className="block mb-2 font-medium">Order Source</label>
              <select
                className="w-full p-2 border rounded focus:border-green-500 focus:ring-green-500 focus:outline-none"
                value={orderSource}
                onChange={(e) => setOrderSource(e.target.value)}
                required
              >
                <option value="provider">Provider</option>
                <option value="patient">Patient</option>
              </select>
              <p className="mt-1 text-sm text-gray-500">
                {orderSource === "patient"
                  ? "Patient-initiated prescription. Provider information is not required."
                  : "Provider-initiated prescription. Provider information will be required."}
              </p>
            </div>
            {/* Prescription Type - Only shown for provider order source */}
            {orderSource === "provider" && (
              <div className="w-full">
                <label className="block mb-2 font-medium">
                  Prescription Type <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full p-2 border rounded focus:border-green-500 focus:ring-green-500 focus:outline-none"
                  value={prescriptionType}
                  onChange={(e) => setPrescriptionType(e.target.value)}
                  required
                >
                  <option value="">Select Prescription Type</option>
                  <option value="VERBAL">Verbal/Phone</option>
                  <option value="WRITTEN">Written/Paper</option>
                </select>
              </div>
            )}
          </div>
          {/* Provider Info Section - Only shown for provider order source */}
          {orderSource === "provider" && (
            <div
              className={`p-3 mt-4 border-b shadow rounded-md ${!prescriptionType ? "opacity-50 pointer-events-none" : ""
                }`}
            >
              <h2 className="mb-2 text-lg font-medium">Provider Info</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="provider_first_name"
                    className="block text-sm font-medium"
                  >
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="provider_first_name"
                    value={providerInfo.first_name}
                    onChange={(e) =>
                      handleProviderInfoChange("first_name", e.target.value)
                    }
                    className="w-full px-2 py-1 border rounded focus:border-green-500 focus:ring-green-500 focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="provider_last_name"
                    className="block text-sm font-medium"
                  >
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="provider_last_name"
                    value={providerInfo.last_name}
                    onChange={(e) =>
                      handleProviderInfoChange("last_name", e.target.value)
                    }
                    className="w-full px-2 py-1 border rounded focus:border-green-500 focus:ring-green-500 focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="provider_title"
                    className="block text-sm font-medium"
                  >
                    Title <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="provider_title"
                    value={providerInfo.title}
                    onChange={(e) =>
                      handleProviderInfoChange("title", e.target.value)
                    }
                    className="w-full px-2 py-1 border rounded focus:border-green-500 focus:ring-green-500 focus:outline-none"
                    required
                  >
                    <option value="">Select Title</option>
                    <option value="MD">MD</option>
                    <option value="DO">DO</option>
                    <option value="NP">NP</option>
                    <option value="PA">PA</option>
                    <option value="RPh">RPh</option>
                    <option value="PharmD">PharmD</option>
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="provider_facility"
                    className="block text-sm font-medium"
                  >
                    Facility <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="provider_facility"
                    value={providerInfo.facility}
                    onChange={(e) =>
                      handleProviderInfoChange("facility", e.target.value)
                    }
                    className="w-full px-2 py-1 border rounded focus:border-green-500 focus:ring-green-500 focus:outline-none"
                    placeholder="e.g., City Hospital, Clinic Name"
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="provider_phone_number"
                    className="block text-sm font-medium"
                  >
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="provider_phone_number"
                    value={providerInfo.phone_number}
                    onChange={(e) =>
                      handleProviderInfoChange("phone_number", e.target.value)
                    }
                    className="w-full px-2 py-1 border rounded focus:border-green-500 focus:ring-green-500 focus:outline-none"
                    placeholder="Provider contact number"
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="provider_id"
                    className="block text-sm font-medium"
                  >
                    Provider ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="provider_id"
                    value={providerInfo.provider_id}
                    onChange={(e) =>
                      handleProviderInfoChange("provider_id", e.target.value)
                    }
                    className="w-full px-2 py-1 border rounded focus:border-green-500 focus:ring-green-500 focus:outline-none"
                    placeholder="Enter provider ID"
                    required
                  />
                </div>
              </div>
            </div>
          )}
          <div
            className={`mt-4 ${!selectedPatientId ||
              (orderSource === "provider" && !isProviderInfoValid())
              ? "opacity-50 pointer-events-none"
              : ""
              }`}
          >
            <h2 className="mb-5 text-lg font-medium">
              Medication Info
            </h2>
            {medications.map((medication, index) => (
              <div
                key={index}
                className="relative p-5 mb-6 bg-white border border-green-100 rounded-lg shadow-sm"
              >
                <div className="absolute px-3 py-1 text-xs text-white bg-green-500 rounded-full -top-3 left-3">
                  Medication #{index + 1}
                </div>
                {medications.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeMedication(index)}
                    className="absolute px-2 py-1 text-xs text-white transition-all duration-200 bg-red-500 rounded-full top-2 right-2 hover:bg-red-600"
                  >
                    Remove
                  </button>
                )}
                <div className="flex flex-col gap-4 mt-2">
                  {/* 1. Medication Name - Fix for direct array format */}
                  <div className="w-full">
                    <label
                      htmlFor={`medication-name-${index}`}
                      className="block text-sm font-medium"
                    >
                      Medication Name <span className="text-red-500">*</span>
                    </label>

                    <div className="relative">
                      <input
                        type="text"
                        id={`medication-name-${index}`}
                        value={medication.custom_medication_name}
                        onFocus={() => {
                          // Only show dropdown if there's text in the input
                          if (medication.custom_medication_name && medication.custom_medication_name.trim() !== '') {
                            setMedications(prevMeds => {
                              const updatedMeds = [...prevMeds];
                              updatedMeds[index] = {
                                ...updatedMeds[index],
                                showMedicationDropdown: true
                              };
                              return updatedMeds;
                            });
                          }
                        }}
                        onChange={(e) => {
                          handleMedicationChange(
                            index,
                            "custom_medication_name",
                            e.target.value
                          );
                          // Show dropdown only when typing
                          setMedications(prevMeds => {
                            const updatedMeds = [...prevMeds];
                            updatedMeds[index] = {
                              ...updatedMeds[index],
                              showMedicationDropdown: e.target.value.trim() !== ''
                            };
                            return updatedMeds;
                          });
                        }}
                        className="border px-2 h-[32px] rounded w-full focus:border-green-500 focus:ring-green-500 focus:outline-none"
                        placeholder="Type to search or add new medication"
                        required
                      />

                      {/* Fixed medication suggestions dropdown that works with direct array format */}
                      {medication.showMedicationDropdown && (
                        <div className="absolute z-50 w-full mt-1 overflow-y-auto bg-white border rounded shadow-md max-h-60">
                          {/* Display status if loading or no data */}
                          {loading ? (
                            <div className="px-3 py-2 text-center text-gray-500">
                              Loading medications...
                            </div>
                          ) : !medicationNames ||
                            (Array.isArray(medicationNames) && medicationNames.length === 0) ||
                            (medicationNames?.data && medicationNames.data.length === 0) ? (
                            <div className="px-3 py-2 text-center text-gray-500">
                              No medication data available
                            </div>
                          ) : (
                            <>
                              {/* Safe rendering of medication options */}
                              {(Array.isArray(medicationNames) ? medicationNames : medicationNames?.data || [])
                                .filter(med => {
                                  const searchTerm = String(medication.custom_medication_name || '').toLowerCase();
                                  const medName = String(med.name || '').toLowerCase();

                                  // If no search term, show all medications
                                  if (!searchTerm) {
                                    return true;
                                  }

                                  // Check if medication name starts with the search term
                                  return medName.startsWith(searchTerm);
                                })
                                .sort((a, b) => {
                                  const nameA = String(a.name || '').toLowerCase();
                                  const nameB = String(b.name || '').toLowerCase();
                                  return nameA.localeCompare(nameB);
                                })
                                .slice(0, 20) // Limit results for performance
                                .map(med => (
                                  <div
                                    key={med.id || `med-${Math.random()}`}
                                    className="px-3 py-2 border-b cursor-pointer hover:bg-green-50"
                                    onClick={() => {
                                      // Extract strength from the medication name if available
                                      const strengthMatch = med.name.match(/(\d+(?:\.\d+)?)\s*(?:mg|g|ml|mL)/i);
                                      const strength = strengthMatch ? strengthMatch[1] : '';

                                      // Extract dosage form from the medication name
                                      const dosageFormMatch = med.name.match(/(Tablet|Capsule|Injection|Syrup|Cream|Ointment|Inhaler)/i);
                                      const dosageForm = dosageFormMatch ? dosageFormMatch[1] : '';

                                      handleMedicationChange(index, "medication_name_id", med.id);
                                      handleMedicationChange(index, "custom_medication_name", med.name);
                                      if (strength) {
                                        handleMedicationChange(index, "strength", strength);
                                      }
                                      if (dosageForm) {
                                        handleMedicationChange(index, "dosage_form", dosageForm);
                                      }
                                      setMedications(prevMeds => {
                                        const updatedMeds = [...prevMeds];
                                        updatedMeds[index] = {
                                          ...updatedMeds[index],
                                          showMedicationDropdown: false
                                        };
                                        return updatedMeds;
                                      });
                                    }}
                                  >
                                    <p className="font-medium">{String(med.name || '')}</p>
                                  </div>
                                ))}

                              {/* Show message if no matching medications */}
                              {medication.custom_medication_name &&
                                (Array.isArray(medicationNames) ? medicationNames : medicationNames?.data || [])
                                  .filter(med => {
                                    const searchTerm = String(medication.custom_medication_name || '').toLowerCase();
                                    const medName = String(med.name || '').toLowerCase();
                                    return medName.startsWith(searchTerm);
                                  }).length === 0 && (
                                  <div className="px-3 py-2 text-center text-gray-500">
                                    No matching medications found
                                  </div>
                                )}
                            </>
                          )}

                          {/* Add new medication option if text is entered */}
                          {medication.custom_medication_name && String(medication.custom_medication_name).trim() !== "" && (
                            <div
                              className="px-3 py-2 bg-green-100 border-b cursor-pointer hover:bg-green-50"
                              onClick={async () => {
                                try {
                                  // Save new medication name to database
                                  const result = await dispatch(createMedicationName({
                                    name: medication.custom_medication_name
                                  })).unwrap();

                                  console.log("Created new medication:", result);

                                  // Update the medication with the new ID
                                  const newMedicationId = result.id || (result.data && result.data.id);

                                  if (!newMedicationId) {
                                    throw new Error("No medication ID received from server");
                                  }

                                  // Update the medication with the new ID
                                  handleMedicationChange(index, "medication_name_id", newMedicationId);

                                  // Keep the name for display
                                  handleMedicationChange(index, "custom_medication_name", medication.custom_medication_name);

                                  toast.success(`Added new medication: ${medication.custom_medication_name}`);

                                  // Refresh medication list
                                  await dispatch(fetchMedicationNames());

                                  // Hide dropdown after selection
                                  setMedications(prevMeds => {
                                    const updatedMeds = [...prevMeds];
                                    updatedMeds[index] = {
                                      ...updatedMeds[index],
                                      showMedicationDropdown: false
                                    };
                                    return updatedMeds;
                                  });
                                } catch (error) {
                                  toast.error(`Failed to add medication: ${error.message || 'Unknown error'}`);
                                  console.error("Error creating medication:", error);
                                }
                              }}
                            >
                              <p className="font-medium text-green-700">+ Add "{String(medication.custom_medication_name)}" as new medication</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {/* Show feedback on medication selection status */}
                    {/* {medication.medication_name_id && (
                      <p className="mt-1 text-xs text-green-600">✓ Selected from database</p>
                      )} */}
                  </div>

                  <div className="flex flex-col w-full gap-4 md:flex-row">
                    {/* 2. Dosage Form */}
                    <div className="w-full">
                      <label
                        htmlFor={`medication-dosage-form-${index}`}
                        className="block text-sm font-medium"
                      >
                        Dosage Form <span className="text-red-500">*</span>
                      </label>
                      <select
                        id={`medication-dosage-form-${index}`}
                        value={medication.dosage_form}
                        onChange={(e) =>
                          handleMedicationChange(
                            index,
                            "dosage_form",
                            e.target.value
                          )
                        }
                        className="border px-2 h-[32px] rounded w-full focus:border-green-500 focus:ring-green-500 focus:outline-none"
                        required
                      >
                        <option value="">Select Dosage Form</option>
                        <option value="Tablet">Tablet</option>
                        <option value="Capsule">Capsule</option>
                        <option value="Lozenge">Lozenge</option>
                        <option value="Effervescent">Effervescent</option>
                        <option value="Syrup">Syrup</option>
                        <option value="Drop">Drop</option>
                        <option value="Inhaler">Inhaler</option>
                        <option value="Ointment">Ointment</option>
                        <option value="Cream">Cream</option>
                        <option value="Gel">Gel</option>
                        <option value="Paste">Paste</option>
                        <option value="Suppository">Suppository</option>
                        <option value="Injection">Injection</option>
                        <option value="Implant">Implant</option>
                        <option value="Powder">Powder</option>
                        <option value="Pessary">Pessary</option>
                      </select>
                    </div>

                    {/* 3. Strength */}
                    <div className="w-full">
                      <label
                        htmlFor={`medication-strength-${index}`}
                        className="block text-sm font-medium"
                      >
                        Strength <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number" // Changed back to number to ensure numeric input
                        id={`medication-strength-${index}`}
                        value={medication.strength}
                        onChange={(e) =>
                          handleMedicationChange(
                            index,
                            "strength",
                            e.target.value
                          )
                        }
                        className="border px-2 h-[32px] rounded w-full focus:border-green-500 focus:ring-green-500 focus:outline-none"
                        placeholder="Enter numeric value only (e.g., 500)" // Updated placeholder
                        required
                      />
                    </div>

                    {/* 7. Unit */}
                    <div className="w-full">
                      <label
                        htmlFor={`medication-unit-${index}`}
                        className="block text-sm font-medium"
                      >
                        Unit <span className="text-red-500">*</span>
                      </label>
                      <select
                        id={`medication-unit-${index}`}
                        value={medication.unit}
                        onChange={(e) =>
                          handleMedicationChange(index, "unit", e.target.value)
                        }
                        className="border px-2 h-[32px] rounded w-full focus:border-green-500 focus:ring-green-500 focus:outline-none"
                        required
                      >
                        <option value="">Select Unit</option>
                        <option value="Gram">Gram</option>
                        <option value="Milligram">Milligram</option>
                        <option value="Microgram">Microgram</option>
                        <option value="Milliequivalent">Milliequivalent</option>
                        <option value="Litre">Litre</option>
                        <option value="Milliliter">Milliliter</option>
                        <option value="Teaspoon">Teaspoon</option>
                        <option value="Tablespoon">Tablespoon</option>
                        <option value="Drop">Drop(s)</option>
                      </select>{" "}
                    </div>
                  </div>

                  <div className="flex flex-col w-full gap-4 md:flex-row">
                    {/* 4. Route */}
                    <div className="w-full">
                      <label
                        htmlFor={`medication-route-${index}`}
                        className="block text-sm font-medium"
                      >
                        Route <span className="text-red-500">*</span>
                      </label>
                      <select
                        id={`medication-route-${index}`}
                        value={medication.route}
                        onChange={(e) =>
                          handleMedicationChange(index, "route", e.target.value)
                        }
                        className="border px-2 h-[32px] rounded w-full focus:border-green-500 focus:ring-green-500 focus:outline-none"
                        required
                      >
                        <option value="">Select Route</option>
                        <option value="Oral">Oral</option>
                        <option value="Sublingual">Sublingual</option>
                        <option value="Buccal">Buccal</option>
                        <option value="Intravenous">Intravenous</option>
                        <option value="Intramuscular">Intramuscular</option>
                        <option value="Subcutaneous">Subcutaneous</option>
                        <option value="Intrathecal">Intrathecal</option>
                        <option value="Topical">Topical</option>
                        <option value="Inhalation">Inhalation</option>
                        <option value="Rectal">Rectal</option>
                        <option value="Intravaginal">Intravaginal</option>
                        <option value="Ophthalmic">Ophthalmic</option>
                        <option value="Otic">Otic</option>
                        <option value="Periodontal">Periodontal</option>
                      </select>
                    </div>

                    {/* 5. Frequency */}
                    <div className="w-full">
                      <label
                        htmlFor={`medication-frequency-${index}`}
                        className="block text-sm font-medium"
                      >
                        Frequency <span className="text-red-500">*</span>
                      </label>
                      <select
                        id={`medication-frequency-${index}`}
                        value={medication.frequency}
                        onChange={(e) =>
                          handleMedicationChange(
                            index,
                            "frequency",
                            e.target.value
                          )
                        }
                        className="border px-2 h-[32px] rounded w-full focus:border-green-500 focus:ring-green-500 focus:outline-none"
                        required
                      >
                        <option value="">Select Frequency</option>
                        <option value="Once daily">Once daily</option>
                        <option value="Twice daily">Twice daily</option>
                        <option value="Three times daily">
                          Three times daily
                        </option>
                        <option value="Four times daily">Four times daily</option>
                        <option value="Every other day">Every other day</option>
                        <option value="As needed">As needed</option>
                        <option value="Once weekly">Once weekly</option>
                        <option value="Every 24 hours">Every 24 hours</option>
                        <option value="Every 12 hours">Every 12 hours</option>
                        <option value="Every 8 hours">Every 8 hours</option>
                        <option value="Before meals">Before meals</option>
                        <option value="After meals">After meals</option>
                        <option value="Every morning">Every morning</option>
                        <option value="Every evening">Every evening</option>
                        <option value="Every night at bedtime">
                          Every night at bedtime
                        </option>
                        <option value="Three times a week">
                          Three times a week
                        </option>
                      </select>
                    </div>

                    {/* 6. Quantity */}
                    <div className="w-full">
                      <label
                        htmlFor={`medication-quantity-${index}`}
                        className="block text-sm font-medium"
                      >
                        Quantity <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        id={`medication-quantity-${index}`}
                        value={medication.quantity}
                        onChange={(e) =>
                          handleMedicationChange(
                            index,
                            "quantity",
                            e.target.value
                          )
                        }
                        className="border px-2 h-[32px] rounded w-full focus:border-green-500 focus:ring-green-500 focus:outline-none"
                        min="1"
                        required
                      />
                    </div>
                  </div>


                  <div className="flex flex-col gap-4 md:flex-row">
                    <div className="flex items-center w-full gap-4">
                      <div className="flex items-center gap-4 mb-2">
                        <label className="text-sm font-medium">Allow Refills</label>
                        <div className="flex items-center gap-2">
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              className="sr-only peer"
                              checked={medication.refills > 0}
                              onChange={(e) => {
                                handleMedicationChange(
                                  index,
                                  "refills",
                                  e.target.checked ? 1 : 0
                                );
                              }}
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                          </label>
                          <span className={`text-sm font-medium ${medication.refills > 0 ? 'text-green-600' : 'text-gray-600'}`}>
                            {medication.refills > 0 ? 'Yes' : 'No'}
                          </span>
                        </div>
                      </div>
                      {medication.refills > 0 && (
                        <div>
                          <label
                            htmlFor={`medication-refills-${index}`}
                            className="block text-sm font-medium"
                          >
                            No. of Refills <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="number"
                            id={`medication-refills-${index}`}
                            value={medication.refills}
                            onChange={(e) =>
                              handleMedicationChange(
                                index,
                                "refills",
                                e.target.value
                              )
                            }
                            className="w-full px-2 py-1 border rounded focus:border-green-500 focus:ring-green-500 focus:outline-none"
                            min="1"
                            required
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 10. Instructions */}
                  <div className="sm:col-span-2">
                    <label
                      htmlFor={`medication-instructions-${index}`}
                      className="block text-sm font-medium"
                    >
                      Instructions <span className="text-gray-500">(Optional)</span>
                    </label>
                    <textarea
                      id={`medication-instructions-${index}`}
                      value={medication.instructions}
                      onChange={(e) =>
                        handleMedicationChange(
                          index,
                          "instructions",
                          e.target.value
                        )
                      }
                      className="w-full h-20 px-2 py-1 border rounded focus:border-green-500 focus:ring-green-500 focus:outline-none"
                      placeholder="e.g., Take one tablet by mouth three times daily with food"
                      required
                    ></textarea>
                  </div>
                </div>
              </div>
            ))}
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={addMedication}
                className="flex items-center px-4 py-1 text-sm text-white transition-all duration-200 bg-green-500 rounded-md shadow-sm hover:bg-green-600"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-4 h-4 mr-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                  />
                </svg>
                Add Another Medication
              </button>
            </div>
          </div>{" "}

          {/* Date and Prescription Details Section - Hidden but functionality remains */}
          {/* <div className="p-4 mt-4 border-b rounded-md shadow">
            <h2 className="mb-2 text-lg font-medium">
              Prescription Details <span className="text-red-500">*</span>
            </h2>
            
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="date_issued" className="block text-sm font-medium">
                  Date Issued <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  id="date_issued"
                  value={date_issued}
                  onChange={(e) => setDate_issued(e.target.value)}
                  className="w-full px-2 py-1 border rounded focus:border-green-500 focus:ring-green-500 focus:outline-none"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">Date the prescription was issued</p>
              </div>
              
              <div>
                <label htmlFor="comments" className="block text-sm font-medium">
                  Notes/Comments
                </label>
                <textarea
                  id="comments"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  className="w-full h-20 px-2 py-1 border rounded focus:border-green-500 focus:ring-green-500 focus:outline-none"
                  placeholder="Any additional notes about this prescription"
                />
              </div>
            </div>
            </div> */}

          <div className="flex justify-end mt-6">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className={`bg-green-500 hover:bg-green-600 text-white py-2 px-6 rounded-md shadow-sm text-sm transition-all duration-200 flex items-center ${loading ? "opacity-80" : ""
                }`}
            >
              {loading ? (
                <>
                  <ColorRing height="20" width="20" />
                  <span className="ml-2">Processing...</span>
                </>
              ) : (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-4 h-4 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  Submit Prescription
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPrescription;
